import { pinSize } from '@lessonup/pin-renderer';
import { Box, newBox, newSize, newVector, Vector } from '@lessonup/pins-shared';
import { InitializedPlayerStoreState, WordCloudView, WordCloudViewAnswer } from '../../playerStore.types';
import { isWordCloudEntry } from '../../utils/entries/entryAssertions';
import { createOrderHandler, OrderHandler } from '../../utils/orderHandler/orderHandler';
import { normalizeAnswer } from '../../utils/pins/wordCloudPlayer.utils';
import { toInputPinPhase } from '../reducers.utils';

function findPreviousAnswer(text: string, previousAnswers: WordCloudViewAnswer[]): WordCloudViewAnswer | undefined {
  return previousAnswers.find((answer) => answer.text === text);
}

export function updateAnswersInView(
  state: InitializedPlayerStoreState,
  previousView: WordCloudView | null,
  orderHandler: OrderHandler
): WordCloudViewAnswer[] {
  const pinId = state.currentPinId;

  const pinState = pinId ? state.pinState[pinId] : null;
  const previousAnswers = pinState?.wordCloud?.answers || [];
  const deletedAnswers = pinState?.wordCloud?.deletedAnswers || [];

  const wordsInEntries = new Map<string, { layout?: Box; order?: string }>();

  state.assignmentEntries.forEach((assignmentEntry) => {
    const studentPinEntry = assignmentEntry.entries[state.currentPinId];
    if (isWordCloudEntry(studentPinEntry) && studentPinEntry?.answer?.words) {
      studentPinEntry.answer.words.forEach((answer) => {
        const normalized = normalizeAnswer(answer);
        if (deletedAnswers.includes(normalized)) return;

        if (!wordsInEntries.has(normalized)) {
          const previous = findPreviousAnswer(normalized, previousAnswers);
          wordsInEntries.set(normalized, { layout: previous?.layout, order: previous?.order });
        }
      });
    }
  });

  const newAnswers: WordCloudViewAnswer[] = [];
  wordsInEntries.forEach((data, normalizedAnswer) => {
    if (deletedAnswers.includes(normalizedAnswer)) return;

    let layout = data.layout;
    let order = data.order;

    if (!layout) {
      layout = newBox(findNewPosition(newAnswers), newSize(0, 0));
    }
    if (!order) {
      order = orderHandler.generateNext();
    }

    newAnswers.push({
      text: normalizedAnswer,
      layout: layout,
      order: order,
    });
  });

  return newAnswers;
}

// To following functions recreate the 1.0 behavior for the answer positioning in the word cloud, this is a temporary solution

const X_DISTANCE_PENALTY = 5; // X is more important to have distance than Y
const MINIMAL_DISTANCE_SCORE = 75;
const MAX_TRIES = 25;

export function findNewPosition(Answers: WordCloudViewAnswer[]): Vector {
  let bestPosition = newVector(0, 0);
  let bestScore = 0;
  let numTry = 0;

  while (numTry < MAX_TRIES) {
    const position = randomPositionOnPin();
    const score = positionScore(Answers, position);

    if (score === 1) return position;

    if (bestScore < score) {
      bestScore = score;
      bestPosition = position;
    }

    numTry = numTry + 1;
  }

  return bestPosition;
}

function positionScore(Answers: WordCloudViewAnswer[], positionToCheck: Vector): number {
  let nearestDistance = Infinity;
  for (const word of Answers) {
    const distance = distanceScore(word, positionToCheck);
    if (distance < nearestDistance) nearestDistance = distance;
  }

  if (nearestDistance > MINIMAL_DISTANCE_SCORE) return 1;
  return nearestDistance / MINIMAL_DISTANCE_SCORE; // so we can choose best option if there is no space
}

function distanceScore(word: WordCloudViewAnswer, positionToCheck: Vector): number {
  if (!word.layout || !word.layout.position) {
    return Infinity; // Return a large distance if position is missing to 'disqualify' the word
  }
  return Math.hypot(
    (word.layout.position.x - positionToCheck.x) / X_DISTANCE_PENALTY,
    word.layout.position.y - positionToCheck.y
  );
}

function randomPositionOnPin(): Vector {
  const margin = 0.1;
  const horizontalOffset = margin * pinSize.width;
  const verticalOffset = margin * pinSize.height;
  return newVector(
    Math.random() * (pinSize.width - horizontalOffset * 2) + horizontalOffset,
    Math.random() * (pinSize.height - verticalOffset * 2) + verticalOffset
  );
}

export function initializeWordCloudPinState(state: InitializedPlayerStoreState, pinId: string) {
  if (!state.pinState[pinId]) {
    state.pinState[pinId] = {
      view: null,
      componentState: {},
    };
  }

  if (!state.pinState[pinId].wordCloud) {
    state.pinState[pinId].wordCloud = {
      answers: [],
      revealedAnswers: [],
      deletedAnswers: [],
      lastOrder: '',
    };
  }

  return state.pinState[pinId];
}

export function syncWordCloudViewWithPinState(state: InitializedPlayerStoreState, pinId: string): WordCloudView {
  if (!state.pinState[pinId] || !state.pinState[pinId].wordCloud || !state.currentView) {
    return { type: 'WORD_CLOUD', phase: 'input' };
  }

  return {
    type: 'WORD_CLOUD',
    phase: toInputPinPhase(state.currentView.phase),
  };
}

export function updateWordCloudAnswerOrder(answers: WordCloudViewAnswer[], answerText: string, lastOrder: string) {
  let newLastOrder = lastOrder;

  try {
    newLastOrder = getNextOrder(lastOrder);
  } catch {
    const orderHandler = createOrderHandler();
    newLastOrder = orderHandler.generateNext();
  }

  const updatedAnswers = answers.map((answer) => {
    if (answer.text === answerText) {
      return {
        ...answer,
        order: newLastOrder,
      };
    }
    return answer;
  });

  return { updatedAnswers, newLastOrder };
}

function getNextOrder(lastOrder: string) {
  try {
    const orderHandler = createOrderHandler({
      start: lastOrder && lastOrder.length > 0 ? lastOrder : 'a0',
    });
    return orderHandler.generateNext();
  } catch {
    const orderHandler = createOrderHandler();
    return orderHandler.generateNext();
  }
}
